// @ts-nocheck
import { useState } from 'react';
import { Button, Card, Code, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { FadeIn, SlideIn, ScaleIn, RotateIn } from '../../../components/animations';

export default function TransitionsPage() {
  const [showFade, setShowFade] = useState(true);
  const [showSlide, setShowSlide] = useState(true);
  const [showScale, setShowScale] = useState(true);
  const [showRotate, setShowRotate] = useState(true);

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Transition Animations</Title>
        <Text c="dimmed" size="sm">
          Smooth entrance and exit animations for elements appearing and disappearing
        </Text>
      </Stack>

      {/* FadeIn */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>FadeIn</Title>
            <Text size="sm" c="dimmed">
              Fade elements in and out with configurable duration and delay
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button onClick={() => setShowFade(!showFade)}>
                Toggle FadeIn
              </Button>
            </Group>
            <FadeIn duration={500} visible={showFade}>
              <Card variant="subtle" p="md">
                <Text>This card fades in smoothly</Text>
              </Card>
            </FadeIn>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<FadeIn duration={500}>
  <Card variant="subtle" p="md">
    <Text>This card fades in smoothly</Text>
  </Card>
</FadeIn>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>With Delay:</Text>
            <SimpleGrid cols={3} spacing="md">
              <FadeIn duration={500} delay={0}>
                <Card variant="outline" p="sm" ta="center">
                  <Text size="sm">No delay</Text>
                </Card>
              </FadeIn>
              <FadeIn duration={500} delay={200}>
                <Card variant="outline" p="sm" ta="center">
                  <Text size="sm">200ms delay</Text>
                </Card>
              </FadeIn>
              <FadeIn duration={500} delay={400}>
                <Card variant="outline" p="sm" ta="center">
                  <Text size="sm">400ms delay</Text>
                </Card>
              </FadeIn>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<FadeIn duration={500} delay={0}>
  <Card>No delay</Card>
</FadeIn>
<FadeIn duration={500} delay={200}>
  <Card>200ms delay</Card>
</FadeIn>
<FadeIn duration={500} delay={400}>
  <Card>400ms delay</Card>
</FadeIn>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • duration - Animation duration in milliseconds (default: 300)
              <br />
              • delay - Delay before animation starts (default: 0)
              <br />
              • easing - CSS easing function (default: 'ease-out')
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* SlideIn */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>SlideIn</Title>
            <Text size="sm" c="dimmed">
              Slide elements in from different directions
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button onClick={() => setShowSlide(!showSlide)}>
                Toggle SlideIn
              </Button>
            </Group>
            <SimpleGrid cols={2} spacing="md">
              <>
                <SlideIn direction="left" duration={500} visible={showSlide}>
                  <Card variant="subtle" p="md" ta="center">
                    <Text size="sm">From Left</Text>
                  </Card>
                </SlideIn>
                <SlideIn direction="right" duration={500} visible={showSlide}>
                  <Card variant="subtle" p="md" ta="center">
                    <Text size="sm">From Right</Text>
                  </Card>
                </SlideIn>
                <SlideIn direction="top" duration={500} visible={showSlide}>
                  <Card variant="subtle" p="md" ta="center">
                    <Text size="sm">From Top</Text>
                  </Card>
                </SlideIn>
                <SlideIn direction="bottom" duration={500} visible={showSlide}>
                  <Card variant="subtle" p="md" ta="center">
                    <Text size="sm">From Bottom</Text>
                  </Card>
                </SlideIn>
              </>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<SlideIn direction="left" duration={500}>
  <Card>From Left</Card>
</SlideIn>
<SlideIn direction="right" duration={500}>
  <Card>From Right</Card>
</SlideIn>
<SlideIn direction="top" duration={500}>
  <Card>From Top</Card>
</SlideIn>
<SlideIn direction="bottom" duration={500}>
  <Card>From Bottom</Card>
</SlideIn>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • direction - 'left' | 'right' | 'top' | 'bottom' (default: 'left')
              <br />
              • distance - Slide distance in pixels (default: 50)
              <br />
              • duration - Animation duration in ms (default: 300)
              <br />
              • delay - Delay before animation starts
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* ScaleIn */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ScaleIn</Title>
            <Text size="sm" c="dimmed">
              Scale elements from small to full size
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button onClick={() => setShowScale(!showScale)}>
                Toggle ScaleIn
              </Button>
            </Group>
            <ScaleIn duration={500} initialScale={0.5} visible={showScale}>
              <Card variant="gradient" gradientVariant="primary" p="md" ta="center">
                <Text c="white" fw={600}>
                  This card scales in from 50%
                </Text>
              </Card>
            </ScaleIn>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<ScaleIn duration={500} initialScale={0.5}>
  <Card variant="gradient" gradientVariant="primary">
    This card scales in from 50%
  </Card>
</ScaleIn>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • initialScale - Starting scale (default: 0.8)
              <br />
              • duration - Animation duration in ms (default: 300)
              <br />
              • delay - Delay before animation starts
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* RotateIn */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>RotateIn</Title>
            <Text size="sm" c="dimmed">
              Rotate elements as they appear
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button onClick={() => setShowRotate(!showRotate)}>
                Toggle RotateIn
              </Button>
            </Group>
            <RotateIn duration={500} degrees={-180} visible={showRotate}>
              <Card variant="subtle" p="md" ta="center">
                <Text fw={600}>This card rotates in</Text>
              </Card>
            </RotateIn>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<RotateIn duration={500} degrees={-180}>
  <Card variant="subtle">
    This card rotates in
  </Card>
</RotateIn>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • degrees - Rotation degrees (default: -360)
              <br />
              • duration - Animation duration in ms (default: 500)
              <br />
              • delay - Delay before animation starts
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Modal/Dialog Entrances</Text>
              <Text size="xs" c="dimmed">
                Use ScaleIn or FadeIn for smooth modal appearances. Combine with SlideIn for side drawers.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>List Item Animations</Text>
              <Text size="xs" c="dimmed">
                Stagger FadeIn or SlideIn animations with delays for sequential list reveals.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Page Transitions</Text>
              <Text size="xs" c="dimmed">
                Apply SlideIn to page content for smooth navigation between views.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Notification Toasts</Text>
              <Text size="xs" c="dimmed">
                Combine SlideIn from top/bottom with FadeIn for attention-grabbing notifications.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
